/**
 * Settings → Online payments (D-133). The agency chooses its primary payment provider and the
 * order of its fallbacks, for the Academy Pass and for its invoices (/pay/), the same for both or
 * not, with exceptions by country. The providers come from the registry (API): a provider added
 * in the backend appears here without any front-end code. Management and Manager only.
 */

import {
  ApiError,
  type PaymentProviderState,
  type PaymentRouting,
  type RoutingRuleRequest,
  getBillingPaymentRoutingListQueryKey,
  useBillingPaymentRoutingList,
  useBillingPaymentRoutingPartialUpdate,
} from "@agencyos/api-client";
import { Badge, Button, GlassCard, SelectField, Skeleton, SoftToggle } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Plus, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { CountrySelect } from "../academy/sales/CountrySelect";
import { SettingsLayout } from "../components/SettingsLayout";
import { fieldErrors } from "../lib/settings";

type Scope = "academy" | "invoices";
type Rule = { primary: string; fallbacks: string[] };
type FullRule = Rule & { countries: Record<string, Rule> };

const INTEGRATIONS = "/settings/integrations";

function asRule(value: PaymentRouting["academy"]): FullRule {
  const countries: Record<string, Rule> = {};
  for (const [code, row] of Object.entries(value?.countries ?? {})) {
    countries[code] = { primary: row.primary, fallbacks: [...row.fallbacks] };
  }
  return { primary: value?.primary ?? "", fallbacks: [...(value?.fallbacks ?? [])], countries };
}

/** The providers usable for a scope (the invoices need a hosted payment page). */
function usableFor(providers: readonly PaymentProviderState[], scope: Scope) {
  return providers.filter((p) => p.scopes.includes(scope));
}

function countryName(code: string, language: string): string {
  try {
    return new Intl.DisplayNames([language], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** Primary + ordered fallbacks; a provider not configured or inactive is greyed, with « Configurer ». */
function ChainEditor({
  rule,
  onChange,
  providers,
  disabled,
}: {
  rule: Rule;
  onChange: (next: Rule) => void;
  providers: PaymentProviderState[];
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const byKey = new Map(providers.map((p) => [p.key, p]));
  const name = (key: string) => byKey.get(key)?.name ?? key;
  const label = (p: PaymentProviderState) => (p.ready ? p.name : `${p.name} (${t("payments.notReady")})`);
  const primary = byKey.get(rule.primary);
  const free = providers.filter((p) => p.key !== rule.primary && !rule.fallbacks.includes(p.key));
  const [adding, setAdding] = useState("");

  const move = (index: number, step: number) => {
    const next = [...rule.fallbacks];
    const [item] = next.splice(index, 1);
    next.splice(index + step, 0, item as string);
    onChange({ ...rule, fallbacks: next });
  };

  return (
    <div className="stack">
      <SelectField
        label={t("payments.primary")}
        hint={t("payments.primaryHint")}
        value={rule.primary}
        disabled={disabled}
        options={[
          ...(primary ? [] : [{ value: "", label: "—" }]),
          ...providers.map((p) => ({ value: p.key, label: label(p) })),
        ]}
        onChange={(e) =>
          onChange({ primary: e.target.value, fallbacks: rule.fallbacks.filter((k) => k !== e.target.value) })
        }
      />
      {primary && !primary.ready ? (
        <p role="note" className="notice-warning">
          {primary.name} : {t("payments.notReady")}.{" "}
          <Link className="link" to={INTEGRATIONS}>
            {t("payments.configure")}
          </Link>
        </p>
      ) : null}
      <div className="stack">
        <strong>{t("payments.fallbacks")}</strong>
        <span className="muted">{t("payments.fallbacksHint")}</span>
        {rule.fallbacks.length ? (
          <ol className="stack" style={{ margin: 0, paddingInlineStart: "1.25rem" }}>
            {rule.fallbacks.map((key, index) => {
              const provider = byKey.get(key);
              const ready = Boolean(provider?.ready);
              return (
                <li key={key} className="row" style={ready ? undefined : { opacity: 0.55 }}>
                  <span>{name(key)}</span>
                  {ready ? null : (
                    <>
                      <Badge>{t("payments.notReady")}</Badge>
                      <Link className="link" to={INTEGRATIONS}>
                        {t("payments.configure")}
                      </Link>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    disabled={disabled || index === 0}
                    aria-label={t("payments.up", { name: name(key) })}
                    onClick={() => move(index, -1)}
                    icon={<ArrowUp aria-hidden size={16} />}
                  />
                  <Button
                    variant="ghost"
                    disabled={disabled || index === rule.fallbacks.length - 1}
                    aria-label={t("payments.down", { name: name(key) })}
                    onClick={() => move(index, 1)}
                    icon={<ArrowDown aria-hidden size={16} />}
                  />
                  <Button
                    variant="ghost"
                    disabled={disabled}
                    aria-label={t("payments.remove", { name: name(key) })}
                    onClick={() => onChange({ ...rule, fallbacks: rule.fallbacks.filter((k) => k !== key) })}
                    icon={<X aria-hidden size={16} />}
                  />
                </li>
              );
            })}
          </ol>
        ) : (
          <span className="muted">{t("payments.noFallback")}</span>
        )}
        {free.length ? (
          <div className="row">
            <SelectField
              label={t("payments.addFallback")}
              value={adding}
              disabled={disabled}
              options={[{ value: "", label: "—" }, ...free.map((p) => ({ value: p.key, label: label(p) }))]}
              onChange={(e) => setAdding(e.target.value)}
            />
            <Button
              variant="secondary"
              disabled={disabled || !adding}
              icon={<Plus aria-hidden size={16} />}
              onClick={() => {
                onChange({ ...rule, fallbacks: [...rule.fallbacks, adding] });
                setAdding("");
              }}
            >
              {t("payments.addFallback")}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function RuleEditor({
  scope,
  title,
  rule,
  onChange,
  providers,
  error,
  disabled,
}: {
  scope: Scope;
  title: string;
  rule: FullRule;
  onChange: (next: FullRule) => void;
  providers: PaymentProviderState[];
  error?: string;
  disabled: boolean;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "fr";
  const [country, setCountry] = useState("");
  const codes = Object.keys(rule.countries).sort();
  return (
    <section className="stack" aria-labelledby={`payments-${scope}`}>
      <h2 id={`payments-${scope}`}>{title}</h2>
      <ChainEditor
        rule={rule}
        providers={providers}
        disabled={disabled}
        onChange={(next) => onChange({ ...rule, ...next })}
      />
      <div className="stack">
        <h3>{t("payments.countries")}</h3>
        <span className="muted">{t("payments.countriesHint")}</span>
        {codes.length ? null : <span className="muted">{t("payments.noCountry")}</span>}
        {codes.map((code) => (
          <GlassCard key={code} as="article" className="stack" aria-label={countryName(code, language)}>
            <div className="row between">
              <strong>{countryName(code, language)}</strong>
              <Button
                variant="ghost"
                disabled={disabled}
                aria-label={t("payments.removeCountry", { country: countryName(code, language) })}
                icon={<X aria-hidden size={16} />}
                onClick={() => {
                  const next = Object.fromEntries(Object.entries(rule.countries).filter(([key]) => key !== code));
                  onChange({ ...rule, countries: next });
                }}
              />
            </div>
            <ChainEditor
              rule={rule.countries[code] as Rule}
              providers={providers}
              disabled={disabled}
              onChange={(next) => onChange({ ...rule, countries: { ...rule.countries, [code]: next } })}
            />
          </GlassCard>
        ))}
        <div className="row">
          <CountrySelect label={t("payments.country")} value={country} onChange={setCountry} />
          <Button
            variant="secondary"
            disabled={disabled || !country || country in rule.countries}
            icon={<Plus aria-hidden size={16} />}
            onClick={() => {
              onChange({ ...rule, countries: { ...rule.countries, [country]: { primary: rule.primary, fallbacks: [...rule.fallbacks] } } });
              setCountry("");
            }}
          >
            {t("payments.addCountry")}
          </Button>
        </div>
      </div>
      {error ? (
        <p role="alert" className="ko">
          {error}
        </p>
      ) : null}
    </section>
  );
}

function ProviderStatus({ providers }: { providers: readonly PaymentProviderState[] }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === "en" ? "en-GB" : "fr-FR";
  const when = (value: string) =>
    new Date(value).toLocaleString(locale, { dateStyle: "short", timeStyle: "short", timeZone: "Africa/Douala" });
  return (
    <GlassCard as="section" className="stack" aria-labelledby="payments-status">
      <h2 id="payments-status">{t("payments.status.title")}</h2>
      <p className="muted">{t("payments.status.intro")}</p>
      <ul className="stack" style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {providers.map((p) => {
          const health = p.health;
          return (
            <li key={p.key} className="stack" style={p.ready ? undefined : { opacity: 0.6 }}>
              <div className="row">
                <strong>{p.name}</strong>
                {!p.configured ? (
                  <Badge>{t("payments.status.notConfigured")}</Badge>
                ) : !p.active ? (
                  <Badge tone="warning">{t("payments.status.inactive")}</Badge>
                ) : p.mode === "live" ? (
                  <Badge tone="danger">{t("payments.status.live")}</Badge>
                ) : (
                  <Badge tone="offer">{p.simulated ? t("payments.status.simulator") : t("payments.status.test")}</Badge>
                )}
                {health && p.active ? (
                  <Badge tone={health.status === "maintenance" ? "danger" : health.status === "degraded" ? "warning" : "success"}>
                    {health.status === "maintenance"
                      ? health.forced
                        ? t("payments.status.forced")
                        : t("payments.status.maintenance")
                      : health.status === "degraded"
                        ? t("payments.status.degraded")
                        : t("payments.status.ok")}
                  </Badge>
                ) : null}
                {p.key_error ? <Badge tone="danger">{t("payments.status.keyError")}</Badge> : null}
                {p.ready ? null : (
                  <Link className="link" to={INTEGRATIONS}>
                    {t("payments.configure")}
                  </Link>
                )}
              </div>
              {p.configured ? (
                <span className="muted">
                  {p.last_test_at
                    ? t("payments.status.lastTest", {
                        date: when(p.last_test_at),
                        result: p.last_test_ok ? t("payments.status.testOk") : t("payments.status.testKo"),
                      })
                    : t("payments.status.neverTested")}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </GlassCard>
  );
}

function RoutingForm({ row }: { row: PaymentRouting }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const save = useBillingPaymentRoutingPartialUpdate({
    mutation: { onSuccess: () => void queryClient.invalidateQueries({ queryKey: getBillingPaymentRoutingListQueryKey() }) },
  });
  const [same, setSame] = useState(row.same_for_both ?? true);
  const [academy, setAcademy] = useState(asRule(row.academy));
  const [invoices, setInvoices] = useState(asRule(row.invoices));
  const errors = fieldErrors(save.error);
  const disabled = save.isPending;
  const customised = row.customised.academy || row.customised.invoices;

  const onSave = () => {
    const data: { same_for_both: boolean; academy: RoutingRuleRequest; invoices?: RoutingRuleRequest } = {
      same_for_both: same,
      academy,
    };
    if (!same) data.invoices = invoices;
    save.mutate({ id: row.id, data });
  };

  return (
    <GlassCard as="section" className="stack">
      {customised ? null : (
        <p role="note" className="muted">
          {t("payments.defaults")}
        </p>
      )}
      <SoftToggle label={t("payments.sameForBoth")} checked={same} onChange={setSame} disabled={disabled} />
      <span className="muted">{t("payments.sameForBothHint")}</span>
      <RuleEditor
        scope="academy"
        title={same ? t("payments.scopes.both") : t("payments.scopes.academy")}
        rule={academy}
        onChange={setAcademy}
        providers={usableFor(row.providers, same ? "invoices" : "academy")}
        error={errors.academy}
        disabled={disabled}
      />
      {same ? (
        <p className="muted">{t("payments.hostedOnly")}</p>
      ) : (
        <RuleEditor
          scope="invoices"
          title={t("payments.scopes.invoices")}
          rule={invoices}
          onChange={setInvoices}
          providers={usableFor(row.providers, "invoices")}
          error={errors.invoices}
          disabled={disabled}
        />
      )}
      {save.isSuccess ? (
        <p role="status" className="ok">
          {t("payments.saved")}
        </p>
      ) : null}
      {save.isError && !Object.keys(errors).length ? (
        <p role="alert" className="ko">
          {t("common.error")}
        </p>
      ) : null}
      <div className="row">
        <Button onClick={onSave} disabled={disabled}>
          {disabled ? t("common.loading") : t("payments.save")}
        </Button>
      </div>
    </GlassCard>
  );
}

export function PaymentsPage() {
  const { t } = useTranslation();
  const list = useBillingPaymentRoutingList();
  let content;
  if (list.error instanceof ApiError && list.error.status === 403)
    content = <GlassCard role="alert">{t("payments.forbidden")}</GlassCard>;
  else if (list.isPending)
    content = (
      <GlassCard aria-busy="true">
        <Skeleton />
      </GlassCard>
    );
  else if (list.data?.status !== 200 || !list.data.data.results[0])
    content = <GlassCard role="alert">{t("common.error")}</GlassCard>;
  else {
    const row = list.data.data.results[0];
    content = (
      <div className="stack">
        <RoutingForm key={row.id} row={row} />
        <ProviderStatus providers={row.providers} />
      </div>
    );
  }
  return (
    <SettingsLayout title={t("payments.title")} intro={t("payments.intro")}>
      {content}
    </SettingsLayout>
  );
}
